import { Component, inject } from '@angular/core';
import {
  MatDialogRef,
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogTitle,
} from '@angular/material/dialog';
import { CommonModule } from '@angular/common';
import {
  ReactiveFormsModule,
  FormsModule,
  FormGroup,
  FormBuilder,
  Validators,
  FormArray,
} from '@angular/forms';
import { MatIcon } from '@angular/material/icon';

@Component({
  selector: 'app-add-station-modal',
  standalone: true,
  imports: [
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatDialogClose,
    ReactiveFormsModule,
    MatIcon,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
  ],
  templateUrl: './add-station-modal.component.html',
  styleUrl: './add-station-modal.component.css',
})
export class AddStationModalComponent {
  readonly dialogRef = inject(MatDialogRef<AddStationModalComponent>);
  readonly data = inject<any>(MAT_DIALOG_DATA);
  stationForm: FormGroup;
  isDuplicate: boolean = false;
  selectedType: any;
  stationTypes: any[] = [];
  disableDefault: boolean = false;
  displayDuplicateError: boolean = false;

  constructor(private fb: FormBuilder) {
    if (this.data.station) {
      this.stationForm = this.fb.group({
        station_name: [
          this.data.station.station_name,
          [Validators.required, Validators.minLength(3), Validators.maxLength(50)],
        ],
        station_type: [
          this.data.station.station_type,
          [Validators.required, Validators.minLength(3), Validators.maxLength(50)],
        ],
        is_default: [{ value: this.data.station.is_default, disabled: this.disableDefault }],
      });
      this.selectedType =
        this.data.station.station_type == 'food_runner'
          ? 'Food Runner'
          : this.data.station.station_type == 'counter'
            ? 'Counter'
            : 'Kitchen';
      if (this.data?.allStations && this.data?.allStations.length > 0) {
        if (
          !this.data?.allStations?.find((f: any) => f.station_type === 'food_runner') ||
          this.data.station.station_type === 'food_runner'
        ) {
          this.stationTypes.push({ displayName: 'Food Runner', value: 'food_runner' });
        }
        if (
          !this.data?.allStations?.find((f: any) => f.station_type === 'counter') ||
          this.data.station.station_type === 'counter'
        ) {
          this.stationTypes.push({ displayName: 'Counter', value: 'counter' });
        }
        if (this.data.station.station_type == 'kitchen') {
          this.stationTypes.push({ displayName: 'Kitchen', value: 'kitchen' });
        }
      }
      if (
        (this.data.station?.station_type == 'kitchen' ||
          this.data.station?.station_type == 'counter' ||
          this.data.station?.station_type == 'food_runner') &&
        this.data.station.is_default
      ) {
        this.stationForm.get('is_default')?.disable();
      }
    } else {
      this.stationForm = this.fb.group({
        station_name: [
          '',
          [Validators.required, Validators.minLength(3), Validators.maxLength(50)],
        ],
        station_type: [
          '',
          [Validators.required, Validators.minLength(3), Validators.maxLength(50)],
        ],
        is_default: [{ value: false, disabled: this.disableDefault }],
      });

      if (this.data?.allStations && this.data?.allStations.length > 0) {
        if (
          !this.data?.allStations?.find((f: any) => f.station_type === 'food_runner') &&
          !this.data.isFromMenu
        ) {
          this.stationTypes.push({ displayName: 'Food Runner', value: 'food_runner' });
        }
        if (
          !this.data?.allStations?.find((f: any) => f.station_type === 'counter') &&
          !this.data.isFromMenu
        ) {
          this.stationTypes.push({ displayName: 'Counter', value: 'counter' });
        }

        this.stationTypes.push({ displayName: 'Kitchen', value: 'kitchen' });

        if (
          this.data?.allStations?.find((f: any) => f.station_type === 'kitchen' && f.is_default)
        ) {
          this.stationForm.get('is_default')?.setValue(false);
        } else if (
          this.data?.allStations?.find((f: any) => f.station_type === 'kitchen' && !f.is_default)
        ) {
          this.stationForm.get('is_default')?.setValue(true);
        }
      } else {
        this.stationTypes = [
          { displayName: 'Kitchen', value: 'kitchen' },
          { displayName: 'Food Runner', value: 'food_runner' },
          { displayName: 'Counter', value: 'counter' },
        ];
      }

      if (this.stationTypes && this.stationTypes.length == 1 && !this.data.station) {
        this.selectedType = this.stationTypes[0].displayName;
        this.stationForm.get('station_type')?.setValue(this.stationTypes[0].value);
      }
    }
  }

  addNewStation() {
    if (!this.isDuplicate) {
      this.dialogRef.close(this.stationForm.value);
    }
  }

  filterStation() {
    if (
      this.data?.allStations?.find(
        (f: any) =>
          f.station_name.trim().toLowerCase() ===
            this.stationForm.get('station_name')?.value.trim().toLowerCase() &&
          f.station_name !== this.data?.station?.station_name,
      )
    ) {
      this.isDuplicate = true;
    } else {
      this.isDuplicate = false;
    }
  }

  changeStationType(station: any) {
    this.selectedType = station.displayName;
    if (station.value === 'counter' || station.value === 'food_runner') {
      this.stationForm.get('is_default')?.setValue(true);
    } else if (
      station.value === 'kitchen' &&
      this.data?.allStations &&
      this.data?.allStations.length < 0
    ) {
      this.stationForm.get('is_default')?.setValue(true);
    } else if (
      station.value === 'kitchen' &&
      this.data?.allStations &&
      this.data?.allStations.length > 0 &&
      this.data?.allStations?.find((f: any) => f.station_type === 'kitchen' && f.is_default)
    ) {
      this.stationForm.get('is_default')?.setValue(false);
    }
    this.stationForm.get('station_type')?.setValue(station.value);
  }

  onDefaultChange(event: Event) {
    const checked = (event.target as HTMLInputElement).checked;
    if (
      checked &&
      this.selectedType == 'Kitchen' &&
      this.data?.allStations?.find((f: any) => f.station_type === 'kitchen' && f.is_default)
    ) {
      this.displayDuplicateError = true;
    } else {
      this.displayDuplicateError = false;
    }
  }
}
